import TransactionCounts from "../../../components/Settings/Statistics/GraphsComponents/TransactionCounts";
import ProductivityTracker from "../../../components/Settings/Statistics/GraphsComponents/ProductivityTracker";
import DebitCreditAmount from "../../../components/Settings/Statistics/GraphsComponents/DebitCreditAmount";
import OdInterestSaved from "../../../components/Settings/Statistics/GraphsComponents/OdInterestSaved";
import CashCoveragePeriod from "../../../components/Settings/Statistics/GraphsComponents/CashCoveragePeriod";
import AccountsPerDay from "../../../components/Settings/Statistics/GraphsComponents/AccountsPerDay";
import IncomeOnIdleFunds from "../../../components/Settings/Statistics/GraphsComponents/IncomeOnIdleFunds";
import TransactionPerDay from "../../../components/Settings/Statistics/GraphsComponents/TransactionPerDay";
import globalText from "../../../utils/globalText";
import BalancePerDay from "../../../components/Settings/Statistics/GraphsComponents/BalancePerDay";

// Widget IDs
export const WIDGET_IDS = {
  TRANSACTION_COUNT: "transaction_count",
  PRODUCTIVITY_TRACKER: "productivity_tracker",
  DEBIT_CREDIT_AMOUNT: "debit_credit_amount",
  OD_INTEREST_SAVED: "od_interest_saved",
  CASH_COVERAGE_PERIOD: "cash_coverage_period",
  ACCOUNTS_PER_DAY: "accounts",
  INCOME_ON_IDLE_FUNDS: "income_on_idle_funds",
  TRANSACTION_PER_DAY: "transactions",
  BALANCE_PER_DAY: "balance_per_day",
};

// Widget id to title lookup table for rendering widget headers
export const WIDGET_TITLES = {
  [WIDGET_IDS.TRANSACTION_COUNT]: "Transactions count",
  [WIDGET_IDS.PRODUCTIVITY_TRACKER]: "Productivity tracker",
  [WIDGET_IDS.DEBIT_CREDIT_AMOUNT]: "Debit and credit amount",
  [WIDGET_IDS.OD_INTEREST_SAVED]: "OD interest saved",
  [WIDGET_IDS.CASH_COVERAGE_PERIOD]: "Cash coverage period",
  [WIDGET_IDS.ACCOUNTS_PER_DAY]: globalText?.ACCOUNTS,
  [WIDGET_IDS.INCOME_ON_IDLE_FUNDS]: "Income on idle funds",
  [WIDGET_IDS.TRANSACTION_PER_DAY]: globalText?.TRANSACTION + "s",
  [WIDGET_IDS.BALANCE_PER_DAY]: globalText?.BALANCE_PER_DAY,
};

// Title to widget id reverse lookup table
export const WIDGET_TITLE_TO_ID = Object.entries(WIDGET_TITLES).reduce(
  (acc, [id, title]) => {
    acc[title] = id;
    acc[title.toLowerCase()] = id;
    return acc;
  },
  {
    /**
     *  Why these aliases exist:
     *  The Statistics sidebar nav labels come from globalText.js (e.g. TEXT?.ACCOUNTS_PER_DAY),
     * while the reverse-lookup keys are auto-generated from WIDGET_TITLES.
     * If a display title in WIDGET_TITLES is updated (e.g. "Accounts per day" → "Accounts"),
     * the new key ("Accounts") is added by the reduce above, but the old string from
     * globalText.js ("Accounts per day") no longer maps to anything.
     * That makes WIDGET_TITLE_TO_ID[value?.name] return undefined, which causes
     * activeWidgetIds.has(undefined) → false, so the home-dashboard icon never shows.
     * Add an alias here for every title that was renamed in WIDGET_TITLES but whose
     * old string is still used as a lookup key elsewhere.
     */
    [globalText?.TRANSACTION_COUNT]: WIDGET_IDS.TRANSACTION_COUNT,
    [globalText?.ACCOUNTS_PER_DAY]: WIDGET_IDS.ACCOUNTS_PER_DAY,
    [globalText?.TRANSACTIONS_PER_DAY]: WIDGET_IDS.TRANSACTION_PER_DAY,
  },
);

// Registry mapping backend widget_ids to React components
export const renderWidget = (widget, flow) => {
  const { widget_id, filters } = widget;

  switch (widget_id) {
    case WIDGET_IDS.TRANSACTION_COUNT:
      return (
        <TransactionCounts
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.PRODUCTIVITY_TRACKER:
      return (
        <ProductivityTracker
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.DEBIT_CREDIT_AMOUNT:
      return (
        <DebitCreditAmount
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.OD_INTEREST_SAVED:
      return (
        <OdInterestSaved
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.CASH_COVERAGE_PERIOD:
      return (
        <CashCoveragePeriod
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.ACCOUNTS_PER_DAY:
      return (
        <AccountsPerDay
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.INCOME_ON_IDLE_FUNDS:
      return (
        <IncomeOnIdleFunds
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.TRANSACTION_PER_DAY:
      return (
        <TransactionPerDay
          flow={flow}
          autoFetch={true}
        />
      );

    case WIDGET_IDS.BALANCE_PER_DAY:
      return (
        <BalancePerDay
          flow="homeDashboard"
          autoFetch={true}
        />
      );

    default:
      return null;
  }
};
